'use client';
import { useCallback, useEffect, useState } from 'react';

import useHuddle01 from './useHuddle01';
import type {
  CustomMediaKind,
  SupportedCodecs,
} from '@huddle01/web-core/types';
import type { Producer } from '@huddle01/web-core/types';

export const useLocalScreenShare = (props?: {
  onProduceStart?: (producer: Producer) => void;
  onProduceClose?: (label: string) => void;
  onProduceError?: () => void;
}) => {
  const { huddleClient } = useHuddle01();

  const localPeer = huddleClient.localPeer;

  const [shareStream, setShareStream] = useState<MediaStream | null>(
    localPeer.getStream({ label: 'screen-share' }),
  );

  const handleStreamFetched = useCallback(
    (data: {
      mediaKind: CustomMediaKind;
      stream: MediaStream;
    }) => {
      if (data.mediaKind === 'screen') {
        setShareStream(data.stream);
      }
    },
    [],
  );

  const handleStreamPlayable = useCallback(
    (data: {
      label: string;
      producer: Producer;
    }) => {
      if (data.label === 'screen-share-video') {
        if (props?.onProduceStart) props?.onProduceStart(data.producer);
      }
      if (data.label === 'screen-share-audio') {
        if (props?.onProduceStart) props?.onProduceStart(data.producer);
      }
    },
    [props?.onProduceStart],
  );

  const handleCloseStream = useCallback(
    (data: {
      label: string;
      mediakind?: CustomMediaKind;
      reason?: { code: number; tag: string; message?: string };
    }) => {
      if (data.label === 'screen-share-video') {
        setShareStream(null);
        if (props?.onProduceClose) props?.onProduceClose(data.label);
      }

      if (data.label === 'screen-share-audio') {
        setShareStream(null);

        if (props?.onProduceClose) props?.onProduceClose(data.label);
      }
    },
    [props?.onProduceClose],
  );

  useEffect(() => {
    localPeer.on('stream-fetched', handleStreamFetched);

    localPeer.on('stream-playable', handleStreamPlayable);

    localPeer.on('stream-closed', handleCloseStream);

    return () => {
      localPeer.off('stream-fetched', handleStreamFetched);

      localPeer.off('stream-playable', handleStreamPlayable);

      localPeer.off('stream-closed', handleCloseStream);
    };
  }, []);

  /**
   * Enables the local screen share  and starts producing the screen sharing stream
   *
   * @param prefferedCodec (Optional) Preferred Codec to be used for the stream ( SupportedCodecs ), make sure the codec is supported by the device ( 'Browser' or 'ReactNative' )
   *
   *`NOTE: You can only produce to a room when you have joined the room, if you try to produce before joining the room it will fetch the stream and start producing when you join the room`
   *
   * @summary This functions handle the producing of media streams to all the remote peers in the room.
   * it enables the local mic fetches the stream opens you mic active indicator light on the device
   * upon successfull fetching of the stream it produces the stream with the label `audio` and `stopTrackOnClose: true` to all the remote peers in the room.
   * when closing using `disableAudio` it will stop the local audio track and close the producer which will notify all the RemotePeers that this producer has stopped producing
   * and they should stop consuming it.
   *
   * @throws { Error } If Preffered Codec is not supported by the Device ( 'Browser' or 'ReactNative' )
   * @throws { Error } If Failed to produce, Reason will be in the error message
   */
  const startScreenShare = useCallback(
    (data?: { prefferedCodec?: SupportedCodecs }) => {
      return localPeer.startScreenShare(data);
    },
    [localPeer],
  );

  const stopScreenShare = localPeer.stopScreenShare;

  const audioTrack = shareStream?.getAudioTracks()[0] || null;

  const videoTrack = shareStream?.getVideoTracks()[0] || null;

  return {
    shareStream,
    startScreenShare,
    stopScreenShare,
    audioTrack,
    videoTrack,
  };
};
